/**
 * Gezielter KV → React-State Sync ohne vollständiges reloadBusinessData.
 * Phase 26: bei NO_KV_BLOB lädt der Listener aus entity_*-Caches.
 */

import { readBestBusinessMergedPayload } from './sharedCore';
import { isEntityStoreNoKvBlobEnabled } from '../infrastructure/storage/businessPayloadMeta';
import { isEntityStoreAuthorityEnabled } from '../infrastructure/storage/entityAggregateStore';
import { purchaseLineEntityId } from './purchaseLineEntityId';
import {
  entityFieldHydrators,
  entityFieldHydrateInFlight,
  registerEntityFieldHydrator
} from './entityFieldHydratorsRegistry';

export { registerEntityFieldHydrator };

export { purchaseLineEntityId } from './purchaseLineEntityId';

export const SYNC_BUSINESS_FIELDS_EVENT = 'syncBusinessFieldsFromKv';

const DEFAULT_FIELDS = [
  'sales',
  'customers',
  'purchases',
  'products',
  'lots',
  'suppliers',
  'orders',
  'invoices',
  'expenses',
  'stockData',
  'expiredProducts',
  'notifications',
  'workerSessions',
  'discrepancyManagement',
  'reports'
];

/**
 * @param {string} businessId
 * @param {string[]} [fields]
 */
export function dispatchSyncBusinessFieldsFromKv(
  businessId,
  fields = DEFAULT_FIELDS
) {
  if (typeof window === 'undefined' || !businessId) return;
  window.dispatchEvent(
    new CustomEvent(SYNC_BUSINESS_FIELDS_EVENT, {
      detail: { businessId, fields }
    })
  );
}

function entityKey(item, ...keys) {
  for (const k of keys) {
    const v = item?.[k];
    if (v != null && v !== '') return String(v);
  }
  return '';
}

export function mergeEntitiesById(prev = [], loaded = [], idKeys = ['id']) {
  const map = new Map();
  for (const item of [
    ...(Array.isArray(loaded) ? loaded : []),
    ...(Array.isArray(prev) ? prev : [])
  ]) {
    const key = entityKey(item, ...idKeys);
    if (!key) continue;
    map.set(key, { ...map.get(key), ...item });
  }
  return Array.from(map.values());
}

function paymentAmount(p) {
  const n = Number(p?.amount ?? p?.totalAmount);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function mergeDebtPaymentLists(a = [], b = []) {
  const map = new Map();
  for (const p of [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])]) {
    if (!p || p.voided === true) continue;
    const id = String(p.id || p.paymentId || '').trim();
    if (id) {
      const prev = map.get(id);
      map.set(id, prev ? { ...prev, ...p } : p);
      continue;
    }
    // Ephemeral fallback key avoids double-dropping legit offline rows without id
    const stamp = String(p.paymentDate || p.createdAt || p.date || '');
    const soft = `soft:${p.customerId || ''}:${paymentAmount(p)}:${stamp}:${p.workerId || ''}`;
    if (!map.has(soft)) map.set(soft, p);
  }
  return Array.from(map.values());
}

function recomputeDebtBalance(rowA = {}, rowB = {}, payments = []) {
  const paidSum = payments.reduce((s, p) => s + paymentAmount(p), 0);
  const baseA =
    Math.max(0, Number(rowA.remainingDebt) || 0) +
    (Array.isArray(rowA.payments) ? rowA.payments : []).reduce((s, p) => s + paymentAmount(p), 0);
  const baseB =
    Math.max(0, Number(rowB.remainingDebt) || 0) +
    (Array.isArray(rowB.payments) ? rowB.payments : []).reduce((s, p) => s + paymentAmount(p), 0);
  const base = Math.max(baseA, baseB, paidSum);
  const remainingDebt = Math.max(0, Math.round((base - paidSum) * 100) / 100);
  return { remainingDebt, paidAmount: Math.round(paidSum * 100) / 100 };
}

/**
 * Customer debts hybrid merge: union of payment events by id, recompute remainingDebt.
 * Plain LWW on remainingDebt alone can drop offline Créance-Einzüge (ADR-0018).
 */
export function mergeCustomerDebtsById(prev = [], loaded = []) {
  const map = new Map();
  const put = (item) => {
    if (!item) return;
    const key =
      entityKey(item, 'customerId', 'id') ||
      String(item.customerId || item.id || '').trim();
    if (!key) return;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, item);
      return;
    }
    const aTs = Date.parse(existing.updatedAt || existing.createdAt || 0) || 0;
    const bTs = Date.parse(item.updatedAt || item.createdAt || 0) || 0;
    const newer = bTs >= aTs ? item : existing;
    const older = newer === item ? existing : item;
    const payments = mergeDebtPaymentLists(older.payments, newer.payments);
    const { remainingDebt, paidAmount } = recomputeDebtBalance(older, newer, payments);
    map.set(key, {
      ...older,
      ...newer,
      payments,
      remainingDebt,
      paidAmount,
      creditUsed: remainingDebt,
      creditAvailable: Math.max(0, (Number(newer.creditLimit ?? older.creditLimit) || 0) - remainingDebt)
    });
  };
  // prev (local) after loaded so concurrent local payments survive unless id-clash
  for (const item of Array.isArray(loaded) ? loaded : []) put(item);
  for (const item of Array.isArray(prev) ? prev : []) put(item);
  return Array.from(map.values());
}

/** Union Achats — React-State gewinnt bei Konflikt (verhindert Lost Purchases nach KV-Sync). */
export function mergePurchasesById(prev = [], loaded = []) {
  const map = new Map();
  for (const item of [
    ...(Array.isArray(loaded) ? loaded : []),
    ...(Array.isArray(prev) ? prev : [])
  ]) {
    const key = purchaseLineEntityId(item);
    if (!key) continue;
    map.set(key, { ...map.get(key), ...item });
  }
  return Array.from(map.values());
}

/** Union Produkte — React-State gewinnt bei Konflikt (frischer Stock nach Achat). */
export function mergeProductsById(prev = [], loaded = []) {
  return mergeEntitiesById(prev, loaded, ['id', 'productId']);
}

/** Union Fournisseurs — React-State gewinnt bei Konflikt. */
export function mergeSuppliersById(prev = [], loaded = []) {
  return mergeEntitiesById(prev, loaded, ['id', 'supplierId']);
}

/** Union Lots — React-State gewinnt bei Konflikt. */
export function mergeLotsById(prev = [], loaded = []) {
  return mergeEntitiesById(prev, loaded, ['id', 'lotId']);
}

/** Union index + Kanon-Blob — bei gleicher ID gewinnt der Blob (zweites Argument). */
export function mergeIndexedWithBlobAuthoritative(indexed = [], blob = [], idKeys = ['id']) {
  return mergeEntitiesById(indexed, blob, ...idKeys);
}

export function normalizeLotsField(lots) {
  if (Array.isArray(lots)) return lots;
  if (lots && typeof lots === 'object') return Object.values(lots);
  return [];
}

export function normalizePurchasesField(purchases) {
  if (Array.isArray(purchases)) return purchases;
  if (purchases && typeof purchases === 'object') return Object.values(purchases);
  return [];
}

/**


/**
 * @param {string} field
 * @param {string} businessId
 */
export function scheduleEntityFieldHydrate(field, businessId) {
  if (!field || !businessId || !isEntityStoreNoKvBlobEnabled()) return;
  const hydrateFn = entityFieldHydrators.get(String(field));
  if (!hydrateFn) return;
  const key = `${field}::${businessId}`;
  if (entityFieldHydrateInFlight.has(key)) return;
  entityFieldHydrateInFlight.add(key);
  Promise.resolve()
    .then(() => hydrateFn(businessId))
    .catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `[businessStateSync] entity hydrate failed for ${field}:`,
          err?.message || err
        );
      }
    })
    .finally(() => {
      entityFieldHydrateInFlight.delete(key);
    });
}

/**
 * Sync-Lesen: Blob hat Vorrang; leerer Cache blockiert den Blob nicht.
 * Phase 26: NO_KV_BLOB → Warm-Cache only (field hydrate via readArrayFieldFromMergedBlob).
 */
export function readSyncWithBlobPriority(businessId, readBlob, getCached, idKeys = ['id']) {
  if (!businessId) return [];
  if (isEntityStoreNoKvBlobEnabled()) {
    const cached = getCached(businessId);
    return cached ?? [];
  }
  const fromBlob = readBlob(businessId);
  const cached = getCached(businessId);
  if (fromBlob.length > 0) {
    if (cached && cached.length > 0) {
      return mergeIndexedWithBlobAuthoritative(cached, fromBlob, ...idKeys);
    }
    return fromBlob;
  }
  if (cached && cached.length > 0) return cached;
  return fromBlob;
}

/**
 * Array-Feld aus Warm-Cache oder Blob (Phase 26: NO_KV_BLOB → Cache + entity hydrate).
 * @param {string} businessId
 * @param {string} field
 * @param {(id: string) => object[]|null|undefined} getCached
 * @param {(raw: unknown) => object[]} [normalize]
 */
export function readArrayFieldFromMergedBlob(
  businessId,
  field,
  getCached,
  normalize = null
) {
  if (!businessId) return [];
  if (isEntityStoreNoKvBlobEnabled() || isEntityStoreAuthorityEnabled()) {
    const cached = getCached?.(businessId);
    if (cached && (!Array.isArray(cached) || cached.length > 0)) return cached;
    if (isEntityStoreNoKvBlobEnabled()) {
      scheduleEntityFieldHydrate(field, businessId);
      if (cached) return cached;
      return [];
    }
  }
  if (isEntityStoreNoKvBlobEnabled()) return [];
  const { parsed } = readBestBusinessMergedPayload(businessId);
  const raw = parsed?.[field];
  if (normalize) return normalize(raw);
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return Object.values(raw);
  return [];
}

/**
 * Root-Dokument aus Warm-Cache oder Blob (stockData, reports, …).
 */
export function readRootDocFieldFromMergedBlob(
  businessId,
  field,
  getCached,
  emptyValue = {}
) {
  if (!businessId) return emptyValue;
  if (isEntityStoreNoKvBlobEnabled() || isEntityStoreAuthorityEnabled()) {
    const cached = getCached?.(businessId);
    if (cached != null) return cached;
  }
  if (isEntityStoreNoKvBlobEnabled()) return emptyValue;
  const { parsed } = readBestBusinessMergedPayload(businessId);
  const value = parsed?.[field];
  if (value == null) return emptyValue;
  return typeof value === 'object' ? value : emptyValue;
}

/** Lots aus entity_*-Cache (NO_KV_BLOB) bzw. Cache + Legacy-Blob. */
export function readLotsFromBusinessPayload(businessId) {
  if (!businessId) return [];
  try {
    // Dynamischer Import vermeidet Zirkel mit lotsReadSource
    const { readLotsSyncForBusiness } = require('./lotsReadSource');
    const fromCache = readLotsSyncForBusiness(businessId);
    if (fromCache.length > 0) return fromCache;
  } catch {
    /* fallback below */
  }
  if (isEntityStoreNoKvBlobEnabled()) return [];
  const { parsed } = readBestBusinessMergedPayload(businessId);
  return normalizeLotsField(parsed?.lots);
}
